import { useRef, useState } from 'react';
import { SideDrawer } from '@/components/ui/SideDrawer';
import { Button } from '@/components/ui/Button';
import { outreachAction, type OutreachEnrollment, type OutreachMessage } from '@/lib/outreach';
import { formatWhen } from './outreach-labels';

interface OutreachReviewProps {
  sequenceId: string;
  enrollments: OutreachEnrollment[];
  titleOf: (recordId: string) => string;
  personOf: (contactId: string) => string;
  onClose: () => void;
  onDone: () => void;
}

const field = 'w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none';

/** An e-mail of the review, saved when the field is left. */
const MessageEditor = ({ message, onError, track }: { message: OutreachMessage; onError: (text: string) => void; track: (save: Promise<unknown>) => void }) => {
  const [subject, setSubject] = useState(message.subject);
  const [body, setBody] = useState(message.body);
  const save = () => {
    if (subject === message.subject && body === message.body) return;
    track(outreachAction({ action: 'update_message', messageId: message.id, subject, body })
      .catch((cause: unknown) => onError(cause instanceof Error ? cause.message : 'Non enregistré.')));
  };
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-domino-inkSoft">{message.stepIndex === 0 ? 'Premier e-mail' : `Relance ${message.stepIndex}`}</p>
      {message.stepIndex === 0 && <input value={subject} aria-label="Objet" onChange={(event) => setSubject(event.target.value)} onBlur={save} className={field} />}
      <textarea rows={message.stepIndex === 0 ? 8 : 4} value={body} aria-label="Texte" onChange={(event) => setBody(event.target.value)} onBlur={save} className={field} />
    </div>
  );
};

/**
 * Read before anything is sent (decision of Benoit, 03/11): every prospect's
 * first e-mail and follow-ups, editable, removable; one confirmation plans them
 * all, the follow-ups then go by themselves until a reply.
 */
export const OutreachReview = ({ sequenceId, enrollments, titleOf, personOf, onClose, onDone }: OutreachReviewProps) => {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  // The count read when the drawer opened: confirmed prospects leave the list, the title stays.
  const [count] = useState(enrollments.length);
  // Edits are saved when a field is left; confirming waits for them, so what is planned is what was read.
  const saves = useRef(new Set<Promise<unknown>>());
  const track = (save: Promise<unknown>) => {
    saves.current.add(save);
    void save.finally(() => saves.current.delete(save));
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await Promise.all([...saves.current]);
      const answer = await outreachAction<{ scheduled: number; firstAt: string | null; lastAt: string | null }>({
        action: 'confirm', sequenceId, enrollmentIds: enrollments.map((enrollment) => enrollment.id),
      });
      setResult(`${answer.scheduled} séquence(s) programmée(s)${answer.firstAt ? ` · premier envoi ${formatWhen(answer.firstAt)}` : ''}${answer.lastAt && answer.lastAt !== answer.firstAt ? ` · dernier ${formatWhen(answer.lastAt)}` : ''}`);
      onDone();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Programmation impossible.');
    } finally {
      setBusy(false);
    }
  };
  const remove = (enrollmentId: string) => outreachAction({ action: 'remove_enrollments', enrollmentIds: [enrollmentId] })
    .then(onDone).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Retrait impossible.'));

  return (
    <SideDrawer label="Relire les e-mails" title={`Relire ${count} séquence${count > 1 ? 's' : ''}`} onClose={onClose}
      actions={!result && (
        <Button size="sm" loading={busy} disabled={busy || enrollments.length === 0} onClick={() => { void confirm(); }}>
          Programmer {enrollments.length}
        </Button>
      )}>
      <div className="space-y-6 p-4">
        {error && <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>}
        {result && <p role="status" className="text-sm font-semibold text-domino-ink">{result}</p>}
        {!result && enrollments.map((enrollment) => (
          <section key={enrollment.id} className="space-y-3 rounded-xl border border-domino-cardBorder p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-domino-ink">{titleOf(enrollment.appRecordId)}</p>
                <p className="text-xs text-domino-inkSoft">{personOf(enrollment.foundContactId)} · {enrollment.toEmail}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { void remove(enrollment.id); }}>Retirer</Button>
            </div>
            {enrollment.messages.map((message) => <MessageEditor key={message.id} message={message} onError={setError} track={track} />)}
          </section>
        ))}
      </div>
    </SideDrawer>
  );
};
